/**
 * Phase 8 · C-32 — Team activity reports service.
 *
 * Powers `/reports/team` (TeamActivityPage + MemberActivityDrawer) with
 * per-member aggregates derived live from existing tables. No aggregation
 * table is introduced — every counter is a live `count`/`select` against
 * the same source-of-truth rows the rest of the app writes.
 *
 * Shape surface (per ticket):
 *   list(orgId, range)      → TeamMember[]      (one row per org member)
 *   getActivity(orgId, …)   → ActivityEvent[]   (50 most recent per member)
 *   getSparkline(orgId, …)  → {date, count}[]   (always 7-day window)
 *   getOrgKpis(orgId, range)→ org-level KPIs    (no per-member fan-out)
 *
 * The ticket referenced `ggj_b2b_org_members` + `auth.users`; this schema
 * uses `ggj_b2b_org_users` (Keycloak-backed, with display_name + email on
 * the row itself — no auth.users join needed).
 */
import { supabase } from '../client';

// ──────────────────────────────────────────────────────────────────────────
// Public types
// ──────────────────────────────────────────────────────────────────────────

export type ReportsTeamRangeKey = '7d' | '30d' | '90d' | 'custom';

export interface ReportsTeamRange {
  key: ReportsTeamRangeKey;
  /** ISO strings. Required for 'custom'; derived from `key` otherwise. */
  from?: string;
  to?: string;
}

export type ReportsTeamMemberStatus =
  | 'active_now'
  | 'today'
  | 'this_week'
  | 'idle';

export type ReportsTeamMemberRole = 'Admin' | 'Member' | 'Viewer';

export interface ReportsTeamMember {
  id: string;
  name: string;
  email: string;
  role: ReportsTeamMemberRole;
  status: ReportsTeamMemberStatus;
  activeSearchesCount: number;
  /** 7-day series, oldest first — computed on the same roundtrip for list rendering. */
  activeSearchesSpark: number[];
  candidatesAddedCount: number;
  shortlistedCount: number;
  outreachSentCount: number;
  outreachReplyRate: number;
  interviewsHeldCount: number;
  placementsCount: number;
  lastActivityAt: string | null;
  lastActivityRoute: string;
}

export interface ReportsActivityEvent {
  id: string;
  /** Maps to the audit verb (created / updated / deleted / status_changed / …). */
  action: string;
  /** Logical object type — matches `ggj_b2b_activities.entity`. */
  entity: string;
  entityId: string | null;
  attributeName: string | null;
  oldValue: string | null;
  newValue: string | null;
  createdAt: string;
  actorName: string;
  payload: Record<string, unknown> | null;
}

export interface ReportsSparklinePoint {
  /** ISO date (YYYY-MM-DD) at midnight UTC for the bucket start. */
  date: string;
  count: number;
}

export interface ReportsOrgKpis {
  activeSearches: number;
  candidatesShortlisted: number;
  outreachSent: number;
  outreachReplyRate: number;
  interviewsHeld: number;
  interviewShowRate: number;
}

// ──────────────────────────────────────────────────────────────────────────
// Range helpers
// ──────────────────────────────────────────────────────────────────────────

const MS_PER_DAY = 86_400_000;

function rangeToBounds(range: ReportsTeamRange): { fromIso: string; toIso: string; fromMs: number; toMs: number } {
  const now = Date.now();
  if (range.key === 'custom' && range.from && range.to) {
    const fromMs = new Date(range.from).getTime();
    const toMs = new Date(range.to).getTime();
    return {
      fromMs,
      toMs,
      fromIso: new Date(fromMs).toISOString(),
      toIso: new Date(toMs).toISOString(),
    };
  }
  const days = range.key === '7d' ? 7 : range.key === '30d' ? 30 : range.key === '90d' ? 90 : 30;
  const fromMs = now - days * MS_PER_DAY;
  return {
    fromMs,
    toMs: now,
    fromIso: new Date(fromMs).toISOString(),
    toIso: new Date(now).toISOString(),
  };
}

function classifyStatus(lastActivityAt: string | null): ReportsTeamMemberStatus {
  if (!lastActivityAt) return 'idle';
  const diff = Date.now() - new Date(lastActivityAt).getTime();
  if (diff < 10 * 60_000) return 'active_now';
  if (diff < 24 * 3_600_000) return 'today';
  if (diff < 7 * MS_PER_DAY) return 'this_week';
  return 'idle';
}

function classifyRole(orgRole: string | null): ReportsTeamMemberRole {
  const r = (orgRole ?? '').toLowerCase();
  if (r === 'owner' || r === 'admin') return 'Admin';
  if (r === 'viewer') return 'Viewer';
  return 'Member';
}

/** Best-effort route for the "last touched" chip; routes unknown entities to the module index. */
function entityRoute(entity: string, entityId: string | null): string {
  const slug = entityId ?? '';
  switch (entity) {
    case 'application':
      return slug ? `/applications/${slug}` : '/applications';
    case 'candidate':
      return slug ? `/candidates/${slug}` : '/candidates';
    case 'client':
      return slug ? `/clients/${slug}` : '/clients';
    case 'job':
      return slug ? `/jobs/${slug}` : '/jobs';
    case 'email_thread':
      return '/email';
    case 'workflow':
      return '/smart-match';
    case 'scheduler':
      return '/scheduler';
    default:
      return '/dashboard';
  }
}

function startOfUtcDay(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function utcDayIso(ms: number): string {
  return new Date(startOfUtcDay(ms)).toISOString().slice(0, 10);
}

// ──────────────────────────────────────────────────────────────────────────
// Row shapes (private)
// ──────────────────────────────────────────────────────────────────────────

interface OrgUserRow {
  id: string;
  keycloak_sub: string | null;
  display_name: string | null;
  email: string | null;
  role: string | null;
  is_active: boolean | null;
}

interface ActivityRow {
  id: string;
  entity: string;
  entity_id: string | null;
  action: string;
  actor_id: string | null;
  actor_keycloak: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

interface ApplicationRow {
  id: string;
  job_id: string | null;
  status: string | null;
  assigned_to: string | null;
  created_at: string | null;
}

interface StatusHistoryRow {
  application_id: string;
  to_status: string | null;
  changed_by: string | null;
  changed_at: string | null;
}

interface CandidateRow {
  id: string;
  created_by: string | null;
  created_at: string | null;
}

interface EmailThreadRow {
  id: string;
  sent_by: string | null;
  sent_at: string | null;
  cta_response: string | null;
  cta_responded_at: string | null;
}

interface InterviewSessionRow {
  id: string;
  created_by_org_user_id: string | null;
  status: string | null;
  completed_at: string | null;
  created_at: string | null;
}

// ──────────────────────────────────────────────────────────────────────────
// Service
// ──────────────────────────────────────────────────────────────────────────

function bumpCount<K extends string | number | symbol>(
  map: Map<K, number>,
  key: K | null | undefined,
  by = 1,
): void {
  if (key == null) return;
  map.set(key, (map.get(key) ?? 0) + by);
}

export const reportsTeamService = {
  async list(orgId: string, range: ReportsTeamRange): Promise<ReportsTeamMember[]> {
    if (!orgId) return [];
    const { fromIso, toIso, fromMs, toMs } = rangeToBounds(range);
    const sparkFromMs = toMs - 7 * MS_PER_DAY;
    const sparkFromIso = new Date(sparkFromMs).toISOString();

    // Fan out — one query per source table; we reduce in JS so the page can
    // ship against the existing schema without touching DB/RPC plumbing.
    const [
      usersRes,
      appsRes,
      statusHistRes,
      candsRes,
      emailsRes,
      interviewsRes,
      activitiesRes,
      sparkRes,
    ] = await Promise.all([
      supabase
        .from('ggj_b2b_org_users')
        .select('id, keycloak_sub, display_name, email, role, is_active')
        .eq('org_id', orgId)
        .eq('is_active', true),
      supabase
        .from('ggj_b2b_applications')
        .select('id, job_id, status, assigned_to, created_at')
        .eq('org_id', orgId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso),
      supabase
        .from('ggj_b2b_application_status_history')
        .select('application_id, to_status, changed_by, changed_at')
        .gte('changed_at', fromIso)
        .lte('changed_at', toIso),
      supabase
        .from('ggj_b2b_candidates')
        .select('id, created_by, created_at')
        .eq('org_id', orgId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso),
      supabase
        .from('ggj_b2b_email_threads')
        .select('id, sent_by, sent_at, cta_response, cta_responded_at')
        .eq('org_id', orgId)
        .gte('sent_at', fromIso)
        .lte('sent_at', toIso),
      supabase
        .from('ggj_b2b_ai_interview_sessions')
        .select('id, created_by_org_user_id, status, completed_at, created_at')
        .eq('org_id', orgId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso),
      supabase
        .from('ggj_b2b_activities')
        .select('id, entity, entity_id, action, actor_id, created_at')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false })
        .limit(1500),
      supabase
        .from('ggj_b2b_activities')
        .select('actor_id, created_at')
        .eq('org_id', orgId)
        .gte('created_at', sparkFromIso)
        .lte('created_at', toIso),
    ]);

    if (usersRes.error) throw new Error(usersRes.error.message);
    if (appsRes.error) throw new Error(appsRes.error.message);
    if (statusHistRes.error) throw new Error(statusHistRes.error.message);
    if (candsRes.error) throw new Error(candsRes.error.message);
    if (emailsRes.error) throw new Error(emailsRes.error.message);
    if (interviewsRes.error) throw new Error(interviewsRes.error.message);
    if (activitiesRes.error) throw new Error(activitiesRes.error.message);
    if (sparkRes.error) throw new Error(sparkRes.error.message);

    const users = (usersRes.data ?? []) as OrgUserRow[];
    const apps = (appsRes.data ?? []) as ApplicationRow[];
    const statusHist = (statusHistRes.data ?? []) as StatusHistoryRow[];
    const cands = (candsRes.data ?? []) as CandidateRow[];
    const emails = (emailsRes.data ?? []) as EmailThreadRow[];
    const interviews = (interviewsRes.data ?? []) as InterviewSessionRow[];
    const activities = (activitiesRes.data ?? []) as Array<
      Pick<ActivityRow, 'id' | 'entity' | 'entity_id' | 'action' | 'actor_id' | 'created_at'>
    >;
    const sparkRows = (sparkRes.data ?? []) as Array<{ actor_id: string | null; created_at: string }>;

    // active searches ≈ distinct job_ids touched via applications assigned to the user
    const activeJobsByUser = new Map<string, Set<string>>();
    for (const a of apps) {
      if (!a.assigned_to || !a.job_id) continue;
      let set = activeJobsByUser.get(a.assigned_to);
      if (!set) {
        set = new Set<string>();
        activeJobsByUser.set(a.assigned_to, set);
      }
      set.add(a.job_id);
    }

    // candidates added — candidates.created_by is a keycloak_sub
    const candidatesBySub = new Map<string, number>();
    for (const c of cands) bumpCount(candidatesBySub, c.created_by);

    // shortlisted — status_history row where to_status matches and application is in our org
    const appOrgIds = new Set(apps.map((a) => a.id));
    const shortlistedByUser = new Map<string, number>();
    for (const h of statusHist) {
      if (h.to_status !== 'shortlisted') continue;
      if (!appOrgIds.has(h.application_id)) continue; // org-scope guard (no org_id on history)
      bumpCount(shortlistedByUser, h.changed_by);
    }

    // placements — applications currently placed within the range + assigned
    const placementsByUser = new Map<string, number>();
    for (const a of apps) {
      if (a.status === 'placed') bumpCount(placementsByUser, a.assigned_to);
    }

    // outreach sent / replied
    const outreachByUser = new Map<string, number>();
    const outreachRepliedByUser = new Map<string, number>();
    for (const e of emails) {
      bumpCount(outreachByUser, e.sent_by);
      if (e.cta_response || e.cta_responded_at) {
        bumpCount(outreachRepliedByUser, e.sent_by);
      }
    }

    // interviews held
    const interviewsByUser = new Map<string, number>();
    for (const it of interviews) {
      if (it.status === 'completed') bumpCount(interviewsByUser, it.created_by_org_user_id);
    }

    // last activity + route
    const lastByUser = new Map<string, { at: string; route: string }>();
    for (const ev of activities) {
      if (!ev.actor_id) continue;
      if (lastByUser.has(ev.actor_id)) continue; // rows are desc → first hit wins
      lastByUser.set(ev.actor_id, {
        at: ev.created_at,
        route: entityRoute(ev.entity, ev.entity_id),
      });
    }

    // 7-day sparkline — per-day activity buckets, oldest first
    const sparkByUser = new Map<string, number[]>();
    const bucketStart = startOfUtcDay(sparkFromMs);
    function bucketIndex(iso: string): number {
      const d = startOfUtcDay(new Date(iso).getTime());
      return Math.floor((d - bucketStart) / MS_PER_DAY);
    }
    for (const row of sparkRows) {
      if (!row.actor_id) continue;
      const idx = bucketIndex(row.created_at);
      if (idx < 0 || idx > 6) continue;
      let series = sparkByUser.get(row.actor_id);
      if (!series) {
        series = [0, 0, 0, 0, 0, 0, 0];
        sparkByUser.set(row.actor_id, series);
      }
      series[idx] = (series[idx] ?? 0) + 1;
    }

    return users.map((u) => {
      const sent = outreachByUser.get(u.id) ?? 0;
      const replied = outreachRepliedByUser.get(u.id) ?? 0;
      const last = lastByUser.get(u.id) ?? null;
      const candidatesAdded = u.keycloak_sub ? (candidatesBySub.get(u.keycloak_sub) ?? 0) : 0;
      const lastAt = last?.at ?? null;

      return {
        id: u.id,
        name: u.display_name ?? u.email ?? 'Unknown',
        email: u.email ?? '',
        role: classifyRole(u.role),
        status: classifyStatus(lastAt),
        activeSearchesCount: activeJobsByUser.get(u.id)?.size ?? 0,
        activeSearchesSpark: sparkByUser.get(u.id) ?? [0, 0, 0, 0, 0, 0, 0],
        candidatesAddedCount: candidatesAdded,
        shortlistedCount: shortlistedByUser.get(u.id) ?? 0,
        outreachSentCount: sent,
        outreachReplyRate: sent > 0 ? Math.round((replied / sent) * 100) : 0,
        interviewsHeldCount: interviewsByUser.get(u.id) ?? 0,
        placementsCount: placementsByUser.get(u.id) ?? 0,
        lastActivityAt: lastAt,
        lastActivityRoute: last?.route ?? '/dashboard',
      };
    });
  },

  async getActivity(
    orgId: string,
    memberId: string,
    range: ReportsTeamRange,
  ): Promise<ReportsActivityEvent[]> {
    if (!orgId || !memberId) return [];
    const { fromIso, toIso } = rangeToBounds(range);

    const [actRes, memberRes] = await Promise.all([
      supabase
        .from('ggj_b2b_activities')
        .select('id, entity, entity_id, action, actor_id, actor_keycloak, payload, created_at')
        .eq('org_id', orgId)
        .eq('actor_id', memberId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso)
        .order('created_at', { ascending: false })
        .limit(50),
      supabase
        .from('ggj_b2b_org_users')
        .select('display_name, email')
        .eq('id', memberId)
        .maybeSingle(),
    ]);

    if (actRes.error) throw new Error(actRes.error.message);
    if (memberRes.error) throw new Error(memberRes.error.message);

    const actorName =
      (memberRes.data?.display_name as string | null) ??
      (memberRes.data?.email as string | null) ??
      'Member';
    const rows = (actRes.data ?? []) as ActivityRow[];

    return rows.map((r) => {
      const payload = (r.payload ?? null) as Record<string, unknown> | null;
      const attr = payload && typeof payload['attribute'] === 'string' ? (payload['attribute'] as string) : null;
      const oldValue =
        payload && payload['old_value'] != null ? String(payload['old_value']) : null;
      const newValue =
        payload && payload['new_value'] != null ? String(payload['new_value']) : null;

      return {
        id: r.id,
        action: r.action,
        entity: r.entity,
        entityId: r.entity_id,
        attributeName: attr,
        oldValue,
        newValue,
        createdAt: r.created_at,
        actorName,
        payload,
      };
    });
  },

  async getSparkline(
    orgId: string,
    memberId: string,
    _range: ReportsTeamRange,
  ): Promise<ReportsSparklinePoint[]> {
    if (!orgId || !memberId) {
      return emptySpark();
    }
    const toMs = Date.now();
    const fromMs = toMs - 7 * MS_PER_DAY;
    const fromIso = new Date(fromMs).toISOString();
    const toIso = new Date(toMs).toISOString();

    const { data, error } = await supabase
      .from('ggj_b2b_activities')
      .select('created_at')
      .eq('org_id', orgId)
      .eq('actor_id', memberId)
      .gte('created_at', fromIso)
      .lte('created_at', toIso);
    if (error) throw new Error(error.message);

    const buckets: number[] = [0, 0, 0, 0, 0, 0, 0];
    const startMs = startOfUtcDay(fromMs);
    for (const row of (data ?? []) as Array<{ created_at: string }>) {
      const idx = Math.floor((startOfUtcDay(new Date(row.created_at).getTime()) - startMs) / MS_PER_DAY);
      if (idx >= 0 && idx <= 6) buckets[idx]++;
    }
    return buckets.map((count, i) => ({
      date: utcDayIso(startMs + i * MS_PER_DAY),
      count,
    }));
  },

  async getOrgKpis(orgId: string, range: ReportsTeamRange): Promise<ReportsOrgKpis> {
    if (!orgId) {
      return {
        activeSearches: 0,
        candidatesShortlisted: 0,
        outreachSent: 0,
        outreachReplyRate: 0,
        interviewsHeld: 0,
        interviewShowRate: 0,
      };
    }
    const { fromIso, toIso } = rangeToBounds(range);

    const [appsRes, statusHistRes, emailsRes, interviewsRes] = await Promise.all([
      supabase
        .from('ggj_b2b_applications')
        .select('id, job_id, status, created_at')
        .eq('org_id', orgId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso),
      supabase
        .from('ggj_b2b_application_status_history')
        .select('application_id, to_status, changed_at')
        .gte('changed_at', fromIso)
        .lte('changed_at', toIso),
      supabase
        .from('ggj_b2b_email_threads')
        .select('id, sent_at, cta_response, cta_responded_at')
        .eq('org_id', orgId)
        .gte('sent_at', fromIso)
        .lte('sent_at', toIso),
      supabase
        .from('ggj_b2b_ai_interview_sessions')
        .select('id, status, created_at, completed_at')
        .eq('org_id', orgId)
        .gte('created_at', fromIso)
        .lte('created_at', toIso),
    ]);

    if (appsRes.error) throw new Error(appsRes.error.message);
    if (statusHistRes.error) throw new Error(statusHistRes.error.message);
    if (emailsRes.error) throw new Error(emailsRes.error.message);
    if (interviewsRes.error) throw new Error(interviewsRes.error.message);

    const apps = (appsRes.data ?? []) as Pick<ApplicationRow, 'id' | 'job_id' | 'status' | 'created_at'>[];
    const statusHist = (statusHistRes.data ?? []) as Array<Pick<StatusHistoryRow, 'application_id' | 'to_status'>>;
    const emails = (emailsRes.data ?? []) as Pick<EmailThreadRow, 'id' | 'cta_response' | 'cta_responded_at'>[];
    const interviews = (interviewsRes.data ?? []) as Array<Pick<InterviewSessionRow, 'id' | 'status'>>;

    const activeSearchJobs = new Set<string>();
    for (const a of apps) {
      if (a.job_id) activeSearchJobs.add(a.job_id);
    }

    // Unique candidates newly shortlisted — derive by joining history → apps we just loaded.
    const appsById = new Map<string, Pick<ApplicationRow, 'id' | 'status'>>();
    for (const a of apps) appsById.set(a.id, a);
    const shortlistedAppIds = new Set<string>();
    for (const h of statusHist) {
      if (h.to_status !== 'shortlisted') continue;
      if (appsById.has(h.application_id)) shortlistedAppIds.add(h.application_id);
    }

    const outreachSent = emails.length;
    const outreachReplied = emails.filter((e) => e.cta_response || e.cta_responded_at).length;

    const interviewsHeld = interviews.filter((it) => it.status === 'completed').length;
    const scheduled = interviews.filter((it) =>
      ['invited', 'in_progress', 'completed', 'cancelled'].includes(it.status ?? ''),
    ).length;

    return {
      activeSearches: activeSearchJobs.size,
      candidatesShortlisted: shortlistedAppIds.size,
      outreachSent,
      outreachReplyRate: outreachSent > 0 ? Math.round((outreachReplied / outreachSent) * 100) : 0,
      interviewsHeld,
      interviewShowRate: scheduled > 0 ? Math.round((interviewsHeld / scheduled) * 100) : 0,
    };
  },
};

function emptySpark(): ReportsSparklinePoint[] {
  const toMs = Date.now();
  const fromMs = toMs - 7 * MS_PER_DAY;
  const startMs = startOfUtcDay(fromMs);
  return Array.from({ length: 7 }, (_, i) => ({
    date: utcDayIso(startMs + i * MS_PER_DAY),
    count: 0,
  }));
}
